
import { defineComponent, ref, computed } from 'vue';
import { 
  IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, 
  IonIcon, IonList, IonItem, IonLabel, IonInput, IonTextarea, 
  IonSelect, IonSelectOption, IonThumbnail, IonListHeader,
  loadingController, toastController
} from '@ionic/vue';
import { close } from 'ionicons/icons';
import { useStore } from '@/store';
import { utils } from '@/composables/utils';
import CommonService from '@/services/CommonService';

export default defineComponent({
  name: 'ProgramIssueReportModal',
  components: {
    IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, 
    IonIcon, IonList, IonItem, IonLabel, IonInput, IonTextarea, 
    IonSelect, IonSelectOption, IonThumbnail, IonListHeader
  },
  props: {
    program: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const { closeModal, openImageModal, formatDate } = utils();
    const user = computed(() => store.state.user);
    
    // Form data
    const issueType = ref('');
    const description = ref('');
    const photo = ref('');
    const photoFile = ref<any>(null);
    const viewMode = ref('form');
    const previousReports = ref<any[]>([]);
    
    // Computed properties
    const programName = computed(() => {
      return `${props.program.institutionNameShort} - ${props.program.name} (${props.program.jupasCode})`;
    });
    
    const isFormValid = computed(() => {
      return issueType.value && description.value.trim().length > 0;
    });
    
    const hasSubmittedReports = ref(false);
    
    // Load previous reports
    const loadPreviousReports = async () => {
      try {
        // This would need to be implemented in the backend
        // For now, we'll just set a placeholder
        previousReports.value = [];
        hasSubmittedReports.value = previousReports.value.length > 0;
      } catch (error) {
        console.error('Error loading previous reports:', error);
      }
    };
    
    // Call this on component mount
    loadPreviousReports();
    
    // Methods
    const onImageFileChange = (e: any) => {
      if (e.target.files && e.target.files[0]) {
        photoFile.value = e.target.files[0];
        const reader = new FileReader();
        reader.onload = (e: any) => {
          photo.value = e.target.result;
        };
        reader.readAsDataURL(photoFile.value);
      }
    };
    
    const clearUploadedImage = () => {
      photo.value = '';
      photoFile.value = null;
    };
    
    const getIssueTypeLabel = (type: string) => {
      const types: Record<string, string> = {
        'inaccurate_info': 'Inaccurate Information',
        'calculation_error': 'Calculation Error',
        'missing_data': 'Missing Data',
        'other': 'Other'
      };
      return types[type] || type;
    };
    
    const submitReport = async () => {
      try {
        const loading = await loadingController.create({});
        await loading.present();
        
        const feedback = `[${getIssueTypeLabel(issueType.value)}] ${description.value}`;
        await CommonService.createNewFeedback(
          user.value.fullName || '',
          user.value.email || '',
          feedback,
          photoFile.value,
          props.program.id
        );
        
        loading.dismiss();
        
        const toast = await toastController.create({
          message: 'Your report has been submitted. Thank you!',
          duration: 3000,
          position: 'bottom'
        });
        toast.present();
        
        // Reset form
        issueType.value = '';
        description.value = '';
        clearUploadedImage();
        
        // Reload previous reports
        await loadPreviousReports();
        
        // Close modal
        closeModal();
      } catch (error) {
        console.error('Error submitting report:', error);
        const toast = await toastController.create({
          message: 'Failed to submit report. Please try again.',
          duration: 3000,
          position: 'bottom',
          color: 'danger'
        });
        toast.present();
      }
    };
    
    return {
      close,
      issueType,
      description,
      photo,
      viewMode,
      previousReports,
      programName,
      isFormValid,
      hasSubmittedReports,
      closeModal,
      onImageFileChange,
      clearUploadedImage,
      submitReport,
      getIssueTypeLabel,
      openImageModal,
      formatDate
    };
  }
});
